import React, { Fragment, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  getCompanies,
  getCompanyEmployee,
  updateCompanyEmployee,
} from "../../actions/companyActions";
import { notifyErrorCompanyEmployeeAssignment } from "../../actions/companyEmployeeActions";
import { getShiftTypes } from "../../actions/shiftTypeActions";
import { getCollectiveLabourAgreements } from "../../actions/claActions";
import Form from "./Form";
import {
  Grid,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  FormHelperText,
} from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteIcon from "@material-ui/icons/Delete";

import { KeyboardDatePicker } from "@material-ui/pickers";
import { AppContext } from "../../contexts";
import { CommuteType, EmploymentType } from "../../types/enums";

import { useTranslation } from "react-i18next";
import moment from "moment";

const UpdateCompanyEmployeeForm = ({
  companyEmail,
  employeeEmail,
  getShiftTypes,
  getCompanyEmployee,
  updateCompanyEmployee,
  notifyErrorCompanyEmployeeAssignment,
}) => {
  const { t } = useTranslation();

  const [state, setState] = useState({
    firstName: "",
    lastName: "",
    employmentType: "",
    commuteType: "",
    isEmployeeInEducation: false,
    isSeniorEmployee: false,
    cardPhoto: "",
    cardNumber: "",
    cardValidity: moment(),
    shiftTypes: [],
    claId: null,
    optionShiftTypes: [],
    optionClas: [],
  });

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleShiftTypeChange = (e) => {
    e.persist();
    setState((prevState) => ({ ...prevState, shiftTypes: e.target.value }));
  };

  const handleFileUploadChange = (e) => {
    e.persist();
    const reader = new FileReader();
    reader.readAsDataURL(e.target.files[0]);

    reader.onload = () =>
      setState((prevState) => ({ ...prevState, cardPhoto: reader.result }));
  };

  const handleCheckboxChange = (e) => {
    e.persist();
    setState((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.checked,
    }));
  };

  const handleChange = (e) => {
    e.persist();
    setState((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setIsLoading(true);
    Promise.all([
      getShiftTypes(),
      getCollectiveLabourAgreements(),
      getCompanyEmployee(companyEmail, employeeEmail),
    ])
      .then(([shiftTypes, clas, companyEmployee]) => {
        companyEmployee = {
          ...companyEmployee,
          ...companyEmployee.companies.find(
            (company) => company.companyEmail === companyEmail,
          ),
        };

        setState((prevState) => ({
          ...prevState,
          ...companyEmployee,
          shiftTypes: (companyEmployee.shiftTypes || []).map(
            (shiftType) => shiftType.id,
          ),
          cardPhoto: Buffer.from(
            companyEmployee.cardPhoto || "",
            "base64",
          ).toString("utf-8"),
          optionShiftTypes: shiftTypes,
          optionClas: clas,
        }));
      })
      .catch(setErrorMessage)
      .finally(() => setIsLoading(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    updateCompanyEmployee(companyEmail, employeeEmail, state)
      .then(() => setSuccessMessage("Bijwerken is voltooid."))
      .catch(setErrorMessage)
      .finally(() => setIsLoading(false));
  };

  const handleNotifyError = () => {
    setIsLoading(true);
    notifyErrorCompanyEmployeeAssignment(companyEmail, employeeEmail, state)
      .then(() => setSuccessMessage("Melding is succesvol verzonden."))
      .catch(setErrorMessage)
      .finally(() => setIsLoading(false));
  };

  return (
    <AppContext.Consumer>
      {(settings) => (
        <Form
          onSubmit={handleSubmit}
          disabled={!!successMessage}
          submitText="Wijzigen"
          isFetching={isLoading}
          successMessage={successMessage}
          errorMessage={errorMessage}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                id="firstName"
                name="firstName"
                label="Voornaam *"
                value={state.firstName}
                onChange={handleChange}
                fullWidth
                disabled
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="lastName"
                name="lastName"
                label="Achternaam *"
                value={state.lastName}
                onChange={handleChange}
                fullWidth
                disabled
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor="shiftTypes">Dienstsoorten *</InputLabel>
              <Select
                id="shiftTypes"
                name="shiftTypes"
                multiple
                value={state.shiftTypes}
                onChange={handleShiftTypeChange}
                fullWidth
              >
                {state.optionShiftTypes.map((shiftType) => (
                  <MenuItem
                    key={shiftType.id}
                    value={shiftType.id}
                    style={{
                      fontWeight:
                        state.shiftTypes.indexOf(shiftType.id) === -1
                          ? "normal"
                          : "500",
                    }}
                  >
                    {shiftType.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Meerdere selecteren is mogelijk</FormHelperText>
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor="employmentType">
                Type dienstverband *
              </InputLabel>
              <Select
                id="employmentType"
                name="employmentType"
                value={state.employmentType}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    employmentType: e.target.value || null,
                  }))
                }
                fullWidth
              >
                {Object.values(EmploymentType).map((v, i) => (
                  <MenuItem
                    key={i}
                    value={v}
                    style={{
                      fontWeight: state.employmentType === v ? "500" : "normal",
                    }}
                  >
                    {t(v)}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor="commuteType">Type vervoer *</InputLabel>
              <Select
                id="commuteType"
                name="commuteType"
                value={state.commuteType}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    commuteType: e.target.value || null,
                  }))
                }
                fullWidth
              >
                {Object.values(CommuteType).map((v, i) => (
                  <MenuItem
                    key={i}
                    value={v}
                    style={{
                      fontWeight: state.commuteType === v ? "500" : "normal",
                    }}
                  >
                    {t(v)}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor="clas">CAO</InputLabel>
              <Select
                id="clas"
                name="clas"
                value={state.claId}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    claId: e.target.value || null,
                  }))
                }
                fullWidth
              >
                <MenuItem value="">
                  <em>Geen</em>
                </MenuItem>
                {state.optionClas.map((cla) => (
                  <MenuItem
                    key={cla.id}
                    value={cla.id}
                    style={{
                      fontWeight:
                        state.claId && state.claId === cla.id
                          ? "500"
                          : "normal",
                    }}
                  >
                    {cla.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="isEmployeeInEducation"
                    name="isEmployeeInEducation"
                    checked={state.isEmployeeInEducation}
                    onChange={handleCheckboxChange}
                  />
                }
                label="Medewerker in opleiding"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="isSeniorEmployee"
                    name="isSeniorEmployee"
                    checked={state.isSeniorEmployee}
                    onChange={handleCheckboxChange}
                  />
                }
                label="Seniormedewerker"
              />
            </Grid>

            {state.cardPhoto && (
              <img
                src={state.cardPhoto}
                style={{
                  width: "100%",
                  height: "100%",
                  margin: "0 auto",
                }}
              />
            )}

            {state.optionShiftTypes
              .filter((s) => state.shiftTypes.includes(s.id))
              .some((s) => s.isCardPhotoRequired) && (
              <Fragment>
                <Grid item xs={12} sm={12}>
                  <input
                    id="cardPhoto"
                    type="file"
                    accept="image/jpeg"
                    onChange={handleFileUploadChange}
                    style={{ display: "none" }}
                  />
                  <label htmlFor="cardPhoto">
                    <Button variant="contained" component="span">
                      <AttachFileIcon />
                      Upload medewerkerspas
                    </Button>
                  </label>

                  {state.cardPhoto !== "" && (
                    <Button
                      component="span"
                      onClick={() =>
                        setState((prev) => ({ ...prev, cardPhoto: "" }))
                      }
                    >
                      <DeleteIcon />
                      Verwijder foto
                    </Button>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="cardNumber"
                    name="cardNumber"
                    label="Pasnummer *"
                    value={state.cardNumber}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <KeyboardDatePicker
                    clearable
                    mask="__-__-____"
                    id="cardValidity"
                    name="cardValidity"
                    label="Pas geldig tot *"
                    value={state.cardValidity}
                    format={"L"}
                    onChange={(m) =>
                      setState((prev) => ({
                        ...prev,
                        cardValidity: m,
                      }))
                    }
                    fullWidth
                  />
                </Grid>
              </Fragment>
            )}
          </Grid>

          {!successMessage &&
            Array.isArray(errorMessage) &&
            errorMessage.length > 0 && (
              <Fragment>
                <br />

                <Grid item xs={12} sm={12}>
                  <div className="alert alert-info">
                    <ul>
                      <li>
                        Indien u problemen ondervindt met het wijzigen van een
                        medewerker kunt u hier een melding van maken bij{" "}
                        {settings.name}. Wij zullen hier zo snel mogelijk zorg
                        voor dragen.
                      </li>
                    </ul>
                  </div>

                  <Button onClick={handleNotifyError} fullWidth>
                    Melding maken
                  </Button>
                </Grid>
              </Fragment>
            )}
        </Form>
      )}
    </AppContext.Consumer>
  );
};

UpdateCompanyEmployeeForm.propTypes = {
  companyEmail: PropTypes.string.isRequired,
  employeeEmail: PropTypes.string.isRequired,
  getShiftTypes: PropTypes.func.isRequired,
  getCompanyEmployee: PropTypes.func.isRequired,
  updateCompanyEmployee: PropTypes.func.isRequired,
  notifyErrorCompanyEmployeeAssignment: PropTypes.func.isRequired,
};

export default connect(null, {
  getShiftTypes,
  getCompanies,
  getCompanyEmployee,
  updateCompanyEmployee,
  notifyErrorCompanyEmployeeAssignment,
})(UpdateCompanyEmployeeForm);



// WEBPACK FOOTER //
// ./src/components/forms/UpdateCompanyEmployeeForm.js